/**
 * Kanban Board Page
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchTasks,
  fetchTask,
  updateTask,
  createSubtask,
  updateSubtask,
  createTask
} from '@/store/taskSlice';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { KanbanBoard } from '@/components/KanbanBoard';
import { CreateTaskModal } from '@/components/CreateTaskModal';
import { Task } from '@/types';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { Search, Filter, MoreHorizontal, Plus } from 'lucide-react';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { userAPI } from '@/api/userAPI';
import { CommentCreateData, Comment } from '@/types/comments';
import { commentAPI } from '@/api/commentAPI';
import { deleteComment, updateComment, formatCommentsForActivity } from '@/utils/commentUtils';
import { ensureHtml, toPlainText } from '@/utils/richText';
import { Loader } from '@/components/Loader';
import toast from 'react-hot-toast';
import { sprintAPI } from '@/api/sprintAPI';
import { mastersAPI, WorkflowStatus, TaskType, Priority, IreorderWorkFlowStatues, IupdateWorkflowStatusPayload } from '@/api/mastersApi';
import FilterDrawer from '@/components/FilterDrawer';
import AssigneeChips from '@/components/AssigneeChips';
import { useProjectPermissions } from '@/hooks/useProjectPermissions';

export const Kanban = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useAppDispatch();
  const {
    tasks,
    loading,
    currentTask,
    subtasks
  } = useAppSelector((state) => state.tasks);
  const { currentProject } = useAppSelector((state) => state.projects);
  const { user: authUser, isAuthenticated, token } = useAppSelector((state) => state.auth);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [boardHeight, setBoardHeight] = useState<number>(0);
  const boardContainerRef = useRef<HTMLDivElement | null>(null);
  
 // Project-level permissions: viewer should be read-only within that project
  const { isViewerOnly } = useProjectPermissions(projectId || null);

  // Calculate board height dynamically to fit screen
  const recomputeBoardHeight = () => {
    if (!boardContainerRef.current) return;
    const rect = boardContainerRef.current.getBoundingClientRect();
    const avail = window.innerHeight - rect.top - 16; // 16px bottom padding
    setBoardHeight(Math.max(avail, 120));
  };

  useEffect(() => {
    // Wait next tick to ensure layout is ready
    setTimeout(recomputeBoardHeight, 0);
    window.addEventListener('resize', recomputeBoardHeight);
    window.addEventListener('orientationchange', recomputeBoardHeight);
    return () => {
      window.removeEventListener('resize', recomputeBoardHeight);
      window.removeEventListener('orientationchange', recomputeBoardHeight);
    };
  }, []);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [memberChips, setMemberChips] = useState<{ id: string; initials: string; color: string; name: string }[]>([]);
  // Assignee overflow behavior handled inside AssigneeChips

  // Share popover handled in ProjectHeader
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const projectKey = currentProject?.key || 'AHM'; // Fallback to AHM if no project key
  const [sprints, setSprints] = useState<any[]>([]);
  const activeSprint = sprints.find((s: any) => s.is_active);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // JIRA-like filter drawer state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Filter drawer controlled minimal state
  const filterRef = useRef<HTMLDivElement | null>(null);
  const filterBtnWrapRef = useRef<HTMLDivElement | null>(null);

  // Master data for filters
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [useFallback, setUseFallback] = useState<boolean>(false);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const defaultTaskTypeId = useMemo(() => {
    if (!taskTypes.length) return undefined;
    return (
      taskTypes.find((type) => type.name?.toLowerCase() === 'task')?.id ||
      taskTypes[0]?.id
    );
  }, [taskTypes]);
  const defaultPriorityId = useMemo(() => {
    if (!priorities.length) return undefined;
    return (
      priorities.find((priority) => priority.key === 'medium')?.id ||
      priorities[0]?.id
    );
  }, [priorities]);

  // Selected filters
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [selectedPriorityIds, setSelectedPriorityIds] = useState<string[]>([]);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<string[]>([]);
  const [selectedSprintIds, setSelectedSprintIds] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [parentOnly, setParentOnly] = useState<boolean>(false);
  const [subtaskOnly, setSubtaskOnly] = useState<boolean>(false);

  // Dynamic data states for modal
  const [relatedWork, setRelatedWork] = useState<any>({ blocks: [], relatesTo: [] });
  const [activity, setActivity] = useState<any[]>([]);
  // Gate initial render until all initial API batches finish
  const [bootLoading, setBootLoading] = useState(true);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [sprintsLoaded, setSprintsLoaded] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);

  useEffect(() => {
    if (!projectId) return;
    if (!isAuthenticated || !token) return;
    dispatch(fetchTasks({ project_id: projectId }));
  }, [dispatch, projectId, isAuthenticated, token]);

  // Load filter masters
  useEffect(() => {
    (async () => {
      try {
        const [ws, types, prios] = await Promise.all([
          mastersAPI.getWorkflowStatusesList(projectId as string),
          mastersAPI.getTaskTypes(),
          mastersAPI.getPriorities(),
        ]);
        if (!ws || ws.statuses.length === 0) {
          setUseFallback(true);
          setWorkflowStatuses([]);
        } else {
          setUseFallback(false);
          setWorkflowStatuses(ws.statuses.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
        setTaskTypes(types || []);
        setPriorities(prios || []);
        setMastersLoaded(true);
      } catch {
        setUseFallback(true);
        setWorkflowStatuses([]);
        setMastersLoaded(true); // fall back ready
      }
    })();
  }, []);

  // Load sprints for this project to enable Complete Sprint action
  useEffect(() => {
    (async () => {
      if (!currentProject?.id) return;
      try {
        const data = await sprintAPI.getSprints(currentProject.id);
        setSprints(data || []);
        setSprintsLoaded(true);
      } catch { }
    })();
  }, [currentProject?.id]);

  // Keyboard shortcut: Shift+F toggles filter drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement;
      const isInput = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable);
      
      if (!isInput && e.key.toLowerCase() === 'f' && (e.shiftKey || e.metaKey)) {
        e.preventDefault();
        setIsFilterOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close filter drawer on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!isFilterOpen) return;
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setIsFilterOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isFilterOpen]);

  // Assignee overflow handled by AssigneeChips component

  // Share popover removed from here (moved to ProjectHeader)

  // Filter drawer positions itself via anchorRef


  const normalizeMemberId = (member: any) => {
    const raw = member?.user_id ?? member?.user?.id ?? member?._id ?? member?.id ?? member?.user_email;
    return raw ? String(raw).trim() : '';
  };

  // Load project members for header chips and modal
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentProject?.id) return;
      try {
        const data = await projectMemberAPI.getProjectMembers(currentProject.id);
        if (!mounted) return;

        // Set project members for the modal
        setProjectMembers(data);

        const colorFromSeed = (seed: string) => {
          const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500', 'bg-yellow-500', 'bg-cyan-500'];
          const sum = Array.from(seed).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
          return colors[sum % colors.length];
        };

        const buildChip = (m: any) => {
          const id = normalizeMemberId(m);
          const name = m.user_display_name || m.username || m.user_email || 'User';
          const initials = (() => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
            return (parts[0][0] + parts[1][0]).toUpperCase();
          })();
          return { id: id || name, initials, color: colorFromSeed(id || name), name };
        };

        const dedupeChips = (list: typeof memberChips) => {
          const unique = new Map<string, typeof list[number]>();
          list.forEach((chip) => {
            const key = chip.id?.toString().trim();
            if (!key) return;
            if (!unique.has(key)) unique.set(key, chip);
          });
          return Array.from(unique.values());
        };

        let chips = data.map(buildChip);
        chips = dedupeChips(chips);

        if ((!chips || chips.length === 0) && authUser?.organization_id) {
          try {
            const orgUsers = await userAPI.getUsers({ organization_id: authUser.organization_id, is_active: true, limit: 100 });
            chips = orgUsers.map((u: any) => {
              const name = u.display_name || `${u.first_name} ${u.last_name}`.trim() || u.username || u.email || 'User';
              const initials = (() => {
                const parts = name.trim().split(/\s+/);
                if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
                return (parts[0][0] + parts[1][0]).toUpperCase();
              })();
              const id = String(u.id);
              return { id, initials, color: colorFromSeed(id || name), name };
            });
            chips = dedupeChips(chips);
          } catch { }
        }
        setMemberChips(chips);
        setMembersLoaded(true);
      } catch { }
    })();
    return () => { mounted = false; };
  }, [currentProject?.id, authUser?.organization_id]);

  // Aggregate boot loading based on tasks and dependent data
  useEffect(() => {
    if (!loading && mastersLoaded && sprintsLoaded && membersLoaded) {
      setBootLoading(false);
    } else {
      setBootLoading(true);
    }
  }, [loading, mastersLoaded, sprintsLoaded, membersLoaded]);

  // Handle task click to open modal
  const handleTaskClick = async (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);

    // Fetch dynamic data for the task using Redux
    try {
      await Promise.all([
        dispatch(fetchTask(task.id)),
        loadComments(task.id)
        // dispatch(fetchSubtasks(task.id))
      ]);

      // Fetch related work and activity
      const [relatedWorkData, activityData] = await Promise.all([
        fetchRelatedWork(task.id),
        fetchTaskActivity(task.id)
      ]);

      setRelatedWork(relatedWorkData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
  };

  // Handle modal close
  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
    setRelatedWork({ blocks: [], relatesTo: [] });
    setActivity([]);
    setComments([]);
  };

  const openSubtaskModal = (subtask: Subtask, _parent: Task) => {
    setIsTaskModalOpen(false);
    setActiveSubtask(subtask);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    // Refresh task data when subtask modal closes to show updated subtask info
    if (selectedTask?.id) {
      dispatch(fetchTask(selectedTask.id));
    }
  };

  // Following BacklogView pattern - newStatus is already the workflow_status_id
  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();

      // Refresh tasks list
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
        // Update selectedTask to reflect the status change
        if (selectedTask?.id === taskId) {
          dispatch(fetchTask(taskId));
        }
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleInlineTaskCreate = useCallback(
    async ({ statusId, isFallback, title, taskTypeId }: { statusId: string; isFallback: boolean; title: string; taskTypeId?: string }) => {
      if (!projectId) {
        toast.error('Select a project before creating tasks.');
        return;
      }

      const taskData: any = {
        title: title.trim(),
        project_id: projectId
      };

      const typeIdToUse = taskTypeId || defaultTaskTypeId;
      if (typeIdToUse) {
        taskData.task_type_id = typeIdToUse;
      }
      if (defaultPriorityId) {
        taskData.priority_id = defaultPriorityId;
      }

      if (isFallback) {
        taskData.status = statusId;
      } else {
        taskData.workflow_status_id = statusId;
      }

      try {
        await dispatch(createTask(taskData)).unwrap();
        await dispatch(fetchTasks({ project_id: projectId }));
      } catch (error) {
        console.error('Failed to create inline task', error);
        throw error;
      }
    },
    [projectId, dispatch, defaultTaskTypeId, defaultPriorityId]
  );

  const handleStatusReorder = useCallback(
    async (payload: {
      draggedStatusId: string;
      newOrder: number;
      previousStatusId: string | null;
      afterStatusId: string | null;
    }) => {
      if (!projectId) {
        toast.error("No project found.");
        return;
      }
  
      const toastId = toast.loading("Reordering statuses...");
  
      try {
        const payloadDetails: IreorderWorkFlowStatues = {
          draggedWorflowStatusId: payload.draggedStatusId,
          prevWorkFlowStatusId: payload.previousStatusId,
          nextWorkFlowStatusId: payload.afterStatusId,
          projectId: projectId,
        };
  
        await mastersAPI.reOrderWorkStatues(payloadDetails);
  
        const workflowStatues = await mastersAPI.getWorkflowStatusesList(
          projectId as string
        );
  
        if (!workflowStatues || workflowStatues.statuses.length === 0) {
          setUseFallback(true);
          setWorkflowStatuses([]);
        } else {
          setUseFallback(false);
          setWorkflowStatuses(
            workflowStatues.statuses.sort(
              (a, b) => (a.order || 0) - (b.order || 0)
            )
          );
        }
  
        toast.success("Status order updated successfully.", { id: toastId });
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to reorder statuses. Please try again.";
        toast.error(message, { id: toastId });
        console.error("Reorder error:", error);
      }
    },
    [projectId]
  );

  const handleAddStatus = useCallback(async (payload: { name: string; color: string; category: string }) => {
    // Log the payload details shared on add status
    if (!projectId) return;
    try {
      // 1. Call API to create the status
      await mastersAPI.createWorkflowStatus(projectId, payload);
      
      // 2. Refresh the overall Kanban workflow statuses for filters
      const ws = await mastersAPI.getWorkflowStatusesList(projectId);
      if (ws && ws.statuses) {
        setWorkflowStatuses(ws.statuses.sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
      
      // 3. Refresh the tasks list
      dispatch(fetchTasks({ project_id: projectId }));
    } catch (e) {
      console.error('Failed to create status:', e);
      // Optional: uncomment to show toast to user
      // toast.error('Failed to add status');
      throw e; // Throw so that KanbanBoard knows the save failed and doesn't close form
    }
  }, [projectId, dispatch]);

  // Sync selectedTask with updated task from Redux state after status update (following BacklogView pattern)
  useEffect(() => {
    if (selectedTask && currentTask && currentTask.id === selectedTask.id) {
      setSelectedTask(currentTask);
    }
  }, [currentTask, selectedTask?.id]);

  const loadComments = async (taskId: string) => {
    try {
      const commentsData = await commentAPI.getComments(taskId);
      setComments(commentsData);

      // Use the utility function to format comments
      const activityData = formatCommentsForActivity(commentsData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
      setActivity([]);
    }
  };


  const handleAddComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
    try {
      const contentHtml = html;
      const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
      const newComment = await commentAPI.createComment({
        task_id: taskId,
        content: contentText,
        content_html: contentHtml,
        content_text: contentText,
        parent_comment_id: parentId
      });

      // Reload comments to refresh the list
      await loadComments(taskId);

      return newComment;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const handleAddSubtask = async (taskId: string, title: string) => {
    try {
      await dispatch(createSubtask({
        taskId,
        data: { title }
      })).unwrap();
    } catch (error) {
      console.error('Failed to add subtask:', error);
    }
  };

  const handleUpdateSubtask = async (taskId: string, subtaskId: string, updates: any) => {
    void taskId; // parameter required by API signature, not used here
    try {
      await dispatch(updateSubtask({
        subtaskId,
        data: updates
      })).unwrap();
    } catch (error) {
      console.error('Failed to update subtask:', error);
    }
  };

  // Complete active sprint from board header (Jira-like) using shared ConfirmDialog
  const handleCompleteActiveSprint = () => {
    if (!activeSprint) return;
    setShowCompleteConfirm(true);
  };

  const confirmCompleteActiveSprint = async () => {
    if (!activeSprint) return;
    setIsCompleting(true);
    try {
      await sprintAPI.completeSprint(activeSprint.id);
      // Refresh sprints and tasks
      if (currentProject?.id) {
        try {
          const data = await sprintAPI.getSprints(currentProject.id);
          setSprints(data || []);
        } catch { }
      }
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
      setShowCompleteConfirm(false);
    } catch (e) {
      console.error('Failed to complete sprint:', e);
      // Use toast if available; fallback alert avoided by spec
    } finally {
      setIsCompleting(false);
    }
  };

  // ADD MISSING HANDLERS
  const handleUpdateDescription = async (taskId: string, description: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { description }
      })).unwrap();

      // Refresh the current task
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update description:', error);
      throw error;
    }
  };

  const handleUpdateAssignee = async (taskId: string, assigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { assigned_to: assigneeId === null ? null : assigneeId }
      })).unwrap();

      // Refresh tasks list and current task
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update assignee:', error);
      throw error;
    }
  };

  const handleUpdateReporter = async (taskId: string, reporterId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { reporter_id: reporterId === null ? null : reporterId }
      })).unwrap();

      // Refresh tasks list and current task
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update reporter:', error);
      throw error;
    }
  };

  const handleAssigneeToggle = (userId: string) => {
    setSelectedAssigneeIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleUpdateComment = async (commentId: string, html: string, plainText?: string) => {
    try {
      await updateComment(commentId, html, plainText);
      // Refresh activity if a task is currently open
      if (selectedTask?.id) {
        await loadComments(selectedTask.id);
        const activityData = await fetchTaskActivity(selectedTask.id);
        setActivity(activityData);
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      throw error;
    }
  };

  const rawLabels: string | string[] | null | undefined = (currentTask as any)?.labels;


  // Prepare task details for the sidebar
  const getTaskDetails = () => {
    if (!currentTask) return {};

    return {
      assignee: currentTask.assigned_to || 'Unassigned',
      labels: Array.isArray(rawLabels)
        ? rawLabels
        : typeof rawLabels === "string"
          ? rawLabels.split(",").map((label: string) => label.trim())
          : [],

      dueDate: currentTask.due_date || 'Not set',
      parent: (currentTask as any).parent_id || 'None',
      team: 'Development Team',
      testTeam: 'QA Team',
      sme: [],
      sprint: currentTask.sprint_id || 'Not set',
      originalEstimate: (currentTask as any).estimate || 'Not set',
      timeRemaining: 'Not set',
      development: 'In Progress',
      reporter: 'Janarthanan',
      organization: 'Health Individual'
    };
  };

  // Unique labels for Labels tab
  const allLabels = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((t: any) => {
      const labels = Array.isArray(t?.labels)
        ? t.labels
        : typeof t?.labels === 'string'
          ? (t.labels as string).split(',').map((l) => l.trim()).filter(Boolean)
          : [];
      labels.forEach((l: string) => set.add(l));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const effectiveStatuses: WorkflowStatus[] = useMemo(() => {
    if (!useFallback) return workflowStatuses;
    return [
      { id: 'todo', name: 'TO DO', order: 1 },
      { id: 'requirements', name: 'REQUIREMENTS SIGN-OFF', order: 2 },
      { id: 'in_progress', name: 'DEV IN PROGRESS', order: 3 },
      { id: 'completed', name: 'DEV COMPLETED', order: 4 },
      { id: 'qa', name: 'QA IN PROGRESS', order: 5 },
    ];
  }, [workflowStatuses, useFallback]);

  // Removed unused status/priority counts

  const activeFilterCount = useMemo(() => {
    const bools = (parentOnly ? 1 : 0) + (subtaskOnly ? 1 : 0);
    const sums = selectedStatusIds.length + selectedPriorityIds.length + selectedTaskTypeIds.length + selectedAssigneeIds.length + selectedSprintIds.length + selectedLabels.length;
    return bools + sums;
  }, [parentOnly, subtaskOnly, selectedStatusIds.length, selectedPriorityIds.length, selectedTaskTypeIds.length, selectedAssigneeIds.length, selectedSprintIds.length, selectedLabels.length]);

  const filteredTasks = useMemo(() => {
    const byId = (v?: string) => (v ? String(v) : '');
    return tasks.filter((t: any) => {
      // Search
      const matchesSearch = (t.title || '').toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      // Parent/Subtask filters
      if (parentOnly && t.parent_id) return false;
      if (subtaskOnly && !t.parent_id) return false;

      // Simple filters
      if (selectedAssigneeIds.length > 0 && !selectedAssigneeIds.includes(byId(t.assigned_to))) return false;
      if (selectedSprintIds.length > 0 && !selectedSprintIds.includes(byId(t.sprint_id))) return false;
      if (selectedPriorityIds.length > 0 && !selectedPriorityIds.includes(byId(t.priority_id))) return false;
      if (selectedTaskTypeIds.length > 0 && !selectedTaskTypeIds.includes(byId(t.task_type_id))) return false;

      // Status filter supports workflow_status_id and legacy status
      if (selectedStatusIds.length > 0) {
        const sid = byId((t as any).workflow_status_id);
        if (sid) {
          if (!selectedStatusIds.includes(sid)) return false;
        } else if ((t as any).status) {
          const legacy = String((t as any).status).toLowerCase();
          const nameMatch = effectiveStatuses.find((s) => selectedStatusIds.includes(s.id) && (s.name || '').toLowerCase().includes(legacy));
          if (!nameMatch) return false;
        } else {
          return false;
        }
      }

      // Labels filter
      if (selectedLabels.length > 0) {
        const labelsArr = Array.isArray(t?.labels)
          ? t.labels
          : typeof t?.labels === 'string'
            ? (t.labels as string).split(',').map((l) => l.trim()).filter(Boolean)
            : [];
        const hasAny = labelsArr.some((l: string) => selectedLabels.includes(l));
        if (!hasAny) return false;
      }

      return true;
    });
  }, [tasks, searchQuery, parentOnly, subtaskOnly, selectedAssigneeIds, selectedSprintIds, selectedPriorityIds, selectedTaskTypeIds, selectedStatusIds, selectedLabels, effectiveStatuses]);

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      // Refresh activity if a task is currently open
      if (selectedTask?.id) {
        await loadComments(selectedTask.id);
        const activityData = await fetchTaskActivity(selectedTask.id);
        setActivity(activityData);
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      throw error;
    }
  };

  const handleUpdateTitle = async (id: string, newTitle: string) => {
    if (!currentTask || !selectedTask) return;

    try {
      await dispatch(updateTask({
        id: id || selectedTask.id,
        data: { title: newTitle }
      })).unwrap();
      const taskId = id || currentTask.id || selectedTask.id;
      // Refresh the current task
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update title:', error);
      toast.error('Failed to update title');
      throw error;
    }
  };

  return (
    <div className="flex flex-col" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Board Header */}
      <div ref={headerRef} className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 overflow-visible relative">
        <div className="flex items-center justify-between min-w-0 overflow-visible">
          <div className="flex items-center gap-4 min-w-0 flex-1 overflow-visible">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="w-64 h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Search board"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div >
            {/* Project users as discs next to search */}
            <div className="max-w-full flex flex-wrap">
            <AssigneeChips items={memberChips.map(u => ({ id: u.id, name: u.name }))} selectedIds={selectedAssigneeIds} onToggle={handleAssigneeToggle} />
            </div>
            {/* Filter button placed where Status dropdown used to be */}
            <div ref={filterBtnWrapRef} className="relative">
              <button onClick={() => setIsFilterOpen((v) => !v)} className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 relative">
                <Filter className="w-4 h-4" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center text-[10px] leading-none font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

          </div>

          <div className="flex items-center gap-2">
            {!isViewerOnly && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="h-8 px-3 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
              title="Create new task"
            >
              <Plus className="w-4 h-4" />
              Create Task
            </button>
            )}
            {activeSprint && !isViewerOnly && (
              <button
                onClick={handleCompleteActiveSprint}
                className="h-8 px-3 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                title="Complete active sprint"
              >
                Complete Sprint
              </button>
            )}
            {/* <button className="p-2 hover:bg-gray-100 rounded">
              <MoreHorizontal className="w-4 h-4 text-gray-500" />
            </button>  */}
          </div>
        </div>
        {isFilterOpen && (
          <FilterDrawer
            isOpen={isFilterOpen}
            anchorRef={filterBtnWrapRef}
            onClose={() => setIsFilterOpen(false)}
            workflowStatuses={effectiveStatuses}
            taskTypes={taskTypes}
            priorities={priorities}
            sprints={sprints as any}
            members={projectMembers as any}
            allLabels={allLabels}
            selectedStatusIds={selectedStatusIds} setSelectedStatusIds={setSelectedStatusIds}
            selectedPriorityIds={selectedPriorityIds} setSelectedPriorityIds={setSelectedPriorityIds}
            selectedTaskTypeIds={selectedTaskTypeIds} setSelectedTaskTypeIds={setSelectedTaskTypeIds}
            selectedSprintIds={selectedSprintIds} setSelectedSprintIds={setSelectedSprintIds}
            selectedAssigneeIds={selectedAssigneeIds} setSelectedAssigneeIds={setSelectedAssigneeIds}
            selectedLabels={selectedLabels} setSelectedLabels={setSelectedLabels}
            parentOnly={parentOnly} setParentOnly={setParentOnly}
            subtaskOnly={subtaskOnly} setSubtaskOnly={setSubtaskOnly}
          />
        )}
      </div>

      <div
        ref={boardContainerRef}
        className="board-scroll"
        style={{
          height: boardHeight ? `${boardHeight}px` : undefined,
          overflowY: 'scroll',
          overflowX: 'scroll',
          overscrollBehavior: 'contain' as any,
          scrollbarGutter: 'stable both-edges' as any,
          position: 'relative',
          width: '100%',
        }}
      >
        {/* Kanban Board */}
        {bootLoading ? (
          <Loader variant="overlay" size="xl" text="Loading board..." />
        ) :  (
          <KanbanBoard 
            tasks={filteredTasks} 
            onTaskClick={handleTaskClick}
            onTaskUpdate={() => {
              if (projectId) {
                dispatch(fetchTasks({ project_id: projectId }));
              }
            }}
            projectId={projectId}
            projectMembers={projectMembers}
            taskTypes={taskTypes}
            priorities={priorities}
            onInlineTaskCreate={handleInlineTaskCreate}
            isViewerOnly={isViewerOnly}
            onStatusReorder={handleStatusReorder}
            onAddStatus={handleAddStatus}
            onStatusRename={async (statusId, newName) => {
              const payloadData:IupdateWorkflowStatusPayload = {
                  new_workflow_status_name:newName,
                  workflow_statues_id:statusId
              }
              if(!projectId){
                throw new Error("No project Id found")
              }
              await mastersAPI.updateWorkflowStatusName(projectId, payloadData);
            }}
          />
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        task={currentTask || selectedTask}
        onStatusUpdate={handleStatusUpdate}
        onAddComment={handleAddComment}
        
        onUpdateDescription={handleUpdateDescription} // Now properly defined
        onUpdateAssignee={handleUpdateAssignee} // Now properly defined
        onUpdateReporter={handleUpdateReporter}
        activity={activity}
        taskDetails={getTaskDetails()}
        projectMembers={projectMembers} // Pass the project members
        comments={comments}
        onDeleteComment={handleDeleteComment}
        onUpdateComment={handleUpdateComment}
        projectKey={projectKey}
        onUpdateTaskTitle={handleUpdateTitle}
        onOpenSubtask={openSubtaskModal}
        workflowStatuses={effectiveStatuses}
        priorities={priorities}
        project={currentProject}
        isViewerOnly={isViewerOnly}
      />

      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={`${projectKey}-${(currentTask as any)?.display_number ?? selectedTask?.display_number ?? ''}`.replace(/-$/, '')}
          parentTitle={currentTask?.title || selectedTask?.title}
          parentTask={(currentTask as any) || (selectedTask as any) || undefined}
          onOpenParent={(_parent) => {
            closeSubtaskModal();
            if (_parent) {
              handleTaskClick(_parent);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={projectKey}
          project={currentProject}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          isViewerOnly={isViewerOnly}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
          }}
          onSubtaskUpdated={(updatedSubtask) => {
            // Update active subtask with fresh data
            setActiveSubtask(updatedSubtask);
            // Refresh task to update subtask list in TaskModal
            const taskIdToFetch = selectedTask?.id || currentTask?.id;
            if (taskIdToFetch) {
              dispatch(fetchTask(taskIdToFetch));
            }
            // Refresh tasks list
            if (projectId) {
              dispatch(fetchTasks({ project_id: projectId }));
            }
          }}
          onDelete={async () => {
            // If you add deleteSubtask thunk later, call here
            setIsSubtaskModalOpen(false);
          }}
        />
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultProjectId={projectId || undefined}
        onSuccess={() => {
          // Refresh tasks after task creation
          if (projectId) {
            dispatch(fetchTasks({ project_id: projectId }));
          }
        }}
      />

      {/* Confirm Complete Sprint Dialog (shared) */}
      {showCompleteConfirm && activeSprint && (
        <ConfirmDialog
          isOpen={showCompleteConfirm}
          title="Complete sprint"
          message="Are you sure you want to complete the active sprint? Incomplete tasks will be moved to backlog."
          confirmText="Complete"
          icon="warning"
          onConfirm={confirmCompleteActiveSprint}
          onCancel={() => setShowCompleteConfirm(false)}
          isLoading={isCompleting}
        />
      )}
    </div>
  );
};

// Mock API functions for features not yet in your Redux slice
const fetchRelatedWork = async (taskId: string) => {
  void taskId; // placeholder until API is wired
  // TODO: Implement this API call
  return {
    blocks: [
      { id: '3287', title: 'Health Individual : Unable to create quote in DIC – ...' },
      { id: '3289', title: 'Individuals-Staging-Plans are not fetching for NLG...' }
    ],
    relatesTo: [
      { id: '3291', title: 'Health Individual : Orient Takaful – Unable to log in ...' }
    ]
  };
};

const fetchTaskActivity = async (taskId: string) => {
  // TODO: Implement this API call
  const comments = await commentAPI.getComments(taskId);
  return comments
};

const addComment = async (taskId: string, comment: string, parentId?: string): Promise<Comment> => {
  const commentData: CommentCreateData = {
    content: comment,
    task_id: taskId,
    parent_comment_id: parentId

  };
  return await commentAPI.createComment(commentData);
};
